/**
 * Device entity class
 */

import moment from 'moment'

/**
 * DeviceFactory
 * returns Device entity class
 *
 * @class DeviceFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Device} class
 *
 * @ngdoc factory
 * @name mwApp.factory:DeviceFactory
 */
/* @ngInject */
export default function DeviceFactory ($http, alertLiteSvc, Entity, m2mMixin, onOffMixin, upDownMixin, EntityCache) {
  /* === Device class === */

  /**
   * Device model
   *
   * @class Device
   * @classdesc Device model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.m2mMixin
   * @mixes models.onOffMixin
   * @mixes models.upDownMixin
   * @return {models.Device} Device entity
   */
  function Device () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  Device.prototype = Object.create(Entity.prototype)
  Device.prototype.constructor = Device

  /* === Device instance methods & fields === */

  Object.assign(Device.prototype, {

    /**
     * instance fields
     *
     * @memberof models.Device#
     * @type {Object}
     */
    _fields: {
      id: Number,
      name: null,
      activeSince: moment,
      deviceRedirectExtension: 'DeviceRedirects',
      enabled: Boolean,
      identifier: String,
      ipAddress: String,
      twoFactorAuth: Boolean,
      location: String,
      macAddress: String,
      networks: ['Network'],
      securityCode: String,
      platformType: String,
      primaryDevice: false,
      sortOrder: Number,
      serialNumber: String,
      staticDevice: false,
      status: Boolean,
      subscriber: 'Subscriber',
      theme: 'Theme',
      timezone: String,
      tzBasedOnIp: true
    },

    /**
     * url template
     *
     * @memberof models.Device#
     * @type {String}
     */
    _urlTemplate: 'api/devices/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.Device#
     * @type {String}
     */
    _publicUrlTemplate: 'devices/{{::id}}',

    /**
     * Assign Device to Subscriber
     *
     * @memberof models.Device#
     * @param {Number} id - Subscriber id
     * @return {Promise} promise
     */
    _assign: function (id) {
      return $http.patch(this._url + '?assign=true', id)
        .catch(alertLiteSvc.alertError)
    },

    /**
     * Unassign Device from anybody
     *
     * @memberof models.Device#
     * @return {Promise} promise
     */
    _unassign: function () {
      return $http.patch(this._url + '?assign=false')
        .catch(alertLiteSvc.alertError)
    },

    /**
     * Set Device as Primary (or not primary), performing PATCH request to the server
     *
     * @memberof models.Device#
     * @return {Promise} promise
     */
    _primary: function (setPrimary) {
      return $http.patch(this._url, setPrimary !== false ? '"ENABLE_STATIC"' : '"DISABLE_STATIC"')
        .then(
          response => {
            this._weakCopy(response.data)
            return response
          }
        )
        .catch(alertLiteSvc.alertError)
    },

    /**
     * Reset Device's IP
     *
     * @memberof models.Device#
     * @return {Promise} promise
     */
    _resetIp: function () {
      return $http.patch(this._url, '"RESET_IP"')
        .then(
          response => {
            this.ipAddress = null
            return response
          }
        )
        .catch(alertLiteSvc.alertError)
    }

  }, m2mMixin('networks'), onOffMixin, upDownMixin('sortOrder'))

  /* === Device static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(Device, Entity)
  Device._name = 'Device'
  Device.init()

  // permissions
  Device.access = {
    view: 'viewDevice editSubscriberDevice |',
    create: 'createDevice',
    edit: 'editDevice editSubscriberDevice |',
    delete: 'deleteDevice'
  }

  // extra
  Device.extra = {
    networks: ['Network'],
    themes: ['Theme'],
    deviceRedirects: ['DeviceRedirects']
  }

  /**
   * Save bunch of devices at once
   * Performing POST request to the server
   *
   * @memberof models.Device
   * @param  {models.Device[]} devices - Settings entities
   * @param  {Boolean} withSubscribers - with subscribers flag
   * @param  {Number}      [packageId] - subscriber's network id, if any
   * @return {Promise} promise
   */
  Device.saveAll = function (devices, withSubscribers, packageId) {
    var cache = new EntityCache()
    let url = this.prototype.$_urlTemplate({})
    url = url[url.length - 1] === '/' ? url.substring(0, url.length - 1) : url

    return $http({
      method: 'POST',
      url: url +
              '?withSubscribers=' + withSubscribers +
              (packageId !== undefined ? '&packageId=' + packageId : ''),
      data: devices.map(d => d._object(cache))
    }).catch(alertLiteSvc.alertError)
  }

  return Device
}
